import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { supabase, isSupabaseConfigured } from '@/lib/supabase';
import { getBackendBaseUrl } from '@/lib/backendUrl';

const AuthContext = createContext(undefined);

const BACKEND_URL = getBackendBaseUrl();

// Track usage event helper (authenticated)
const trackEvent = async (eventType, metadata, accessToken) => {
  if (!accessToken) return;
  
  try {
    await fetch(`${BACKEND_URL}/api/track/event`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${accessToken}`,
      },
      body: JSON.stringify({
        event_type: eventType,
        metadata: metadata || {},
      }),
    });
  } catch (e) {
    console.error('Failed to track event:', e);
  }
};

// Track public event (no auth required - for signup/login)
const trackPublicEvent = async (eventType, userId, userEmail, metadata) => {
  try {
    await fetch(`${BACKEND_URL}/api/track/public`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        event_type: eventType,
        user_id: userId,
        user_email: userEmail,
        metadata: metadata || {},
      }),
    });
  } catch (e) {
    console.error('Failed to track public event:', e);
  }
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);
  const [authModalMode, setAuthModalMode] = useState('signup');
  // Invitation-gate error surfaced in AuthModal when a Supabase-authed
  // user has no matching waitlist/org-member record. See checkAccess().
  // Cluster C-2 (Feb 06 2026): now a structured `{ code, params }`
  // payload (string accepted for legacy callers); AuthModal's
  // `gateMessageFor` translates it.
  const [authError, setAuthError] = useState('');

  // QA hook for the C-I1 invitation_required smoke. Lets the testing
  // harness fire setAuthError with a structured payload without
  // provisioning an invite-less Supabase user. Mirrors the existing
  // `window.__BAYENAH_QA_TRIGGER_REAUTH` affordance in apiFetch.js.
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    window.__BAYENAH_QA_FIRE_AUTH_ERROR = (payload) => {
      setAuthError(payload || { code: 'invitation_required' });
      setIsAuthModalOpen(true);
    };
    return () => {
      delete window.__BAYENAH_QA_FIRE_AUTH_ERROR;
    };
  // Mounted-once: setters from useState are stable, so the effect
  // does not need to re-run when their identities are referenced.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Invitation gate. Calls the backend `/api/auth/check-access` endpoint
  // after every successful Supabase auth event. If the authenticated
  // email is NOT in `waitlist_requests` (status="activated") or
  // `org_members` (status="active"), we sign the user out immediately
  // and reopen the modal with a clear "by invitation only" message.
  //
  // Fails OPEN on network/server error — we'd rather let an invited
  // user in than lock everyone out if the gate endpoint hiccups. The
  // backend logs the call separately so misses are visible in Mongo.
  const checkAccess = useCallback(async (currentSession) => {
    if (!currentSession?.access_token) return true;
    try {
      const res = await fetch(`${BACKEND_URL}/api/auth/check-access`, {
        headers: { Authorization: `Bearer ${currentSession.access_token}` },
      });
      if (!res.ok) {
        console.warn('[Bayenah] check-access non-OK status:', res.status);
        return true; // fail open
      }
      const data = await res.json();
      if (data?.access) return true;

      // Blocked — sign the user out and surface the message.
      console.warn('[Bayenah] check-access denied:', data?.reason);
      try { await supabase.auth.signOut({ scope: 'global' }); } catch (_e) { /* ignore */ }
      setUser(null);
      setSession(null);
      setAuthError({
        // C-2 (Feb 06 2026): structured code so AuthModal can render
        // the localized copy from its `t` table instead of a hardcoded
        // English string. See AuthModal.jsx `gateMessageFor`.
        code: 'invitation_required',
      });
      setAuthModalMode('login');
      setIsAuthModalOpen(true);
      // Bounce off any protected route — the modal lives on /
      if (typeof window !== 'undefined' && window.location.pathname !== '/') {
        // Sprint 11.3 (2026-06-13) — instrumented so the operator can
        // see exactly which redirect site fires the post-login loop
        // in their browser DevTools. Search the console for the
        // `[AUTH-REDIRECT]` prefix; whichever line logs first is the
        // bounce-to-landing culprit.
        try {
          console.warn('[AUTH-REDIRECT] AuthContext.checkAccess → window.location.href = "/"',
            { reason: 'check-access returned access:false', from: window.location.pathname });
        } catch (_e) { /* noop */ }
        window.location.href = '/';
      }
      return false;
    } catch (err) {
      console.error('[Bayenah] check-access failed:', err);
      return true; // fail open on error
    }
  }, []);

  // Initialize auth state on mount
  useEffect(() => {
    if (!isSupabaseConfigured()) {
      console.warn('Supabase not configured - auth disabled');
      setLoading(false);
      return;
    }

    // Magic-link error surface (May-19 2026):
    //   When Supabase rejects a token (expired, already-used, invalid)
    //   it redirects to `redirect_to` with the failure encoded in the
    //   URL hash, e.g.
    //     #error=access_denied&error_code=otp_expired&error_description=...
    //   Without this guard the user lands on a silent landing page with
    //   no explanation. We translate `otp_expired` (the common case for
    //   email-scanner pre-fetch and double-click) into the invitation
    //   modal with a clear message, and clear the hash so refreshing
    //   the page doesn't re-trigger the same error banner forever.
    if (typeof window !== 'undefined' && window.location.hash) {
      const hashParams = new URLSearchParams(window.location.hash.replace(/^#/, ''));
      const errCode = hashParams.get('error_code') || hashParams.get('error');
      if (errCode) {
        const descRaw = hashParams.get('error_description') || '';
        // Supabase URL-encodes spaces as '+'. decodeURIComponent
        // doesn't translate '+' → ' ', so we normalize first, then
        // decode the rest. Wrapped in try/catch so a malformed
        // sequence (rare but possible from third-party providers)
        // doesn't crash the boot path.
        let desc = descRaw.replace(/\+/g, ' ');
        try {
          desc = decodeURIComponent(desc);
        } catch {
          /* keep the plus-normalized value */
        }
        // C-2 (Feb 06 2026): pass a structured code/params bundle so
        // AuthModal's `gateMessageFor` can render the localized
        // version.
        setAuthError(
          errCode === 'otp_expired'
            ? { code: 'otp_expired' }
            : {
              code: 'oauth_failed',
              params: { errCode, desc },
            },
        );
        setIsAuthModalOpen(true);
        try {
          window.history.replaceState(null, '', window.location.pathname + window.location.search);
        } catch (_e) { /* ignore */ }
      }
    }

    // Get initial session
    supabase.auth.getSession().then(async ({ data: { session: initialSession } }) => {
      if (initialSession?.user) {
        // Gate first — bail out if the user is not on the invite list.
        const ok = await checkAccess(initialSession);
        if (!ok) {
          setLoading(false);
          return;
        }
        setSession(initialSession);
        setUser({
          id: initialSession.user.id,
          email: initialSession.user.email,
          name: initialSession.user.user_metadata?.full_name || 
                initialSession.user.user_metadata?.name ||
                initialSession.user.email?.split('@')[0],
          avatar: initialSession.user.user_metadata?.avatar_url || 
                  initialSession.user.user_metadata?.picture,
          provider: initialSession.user.app_metadata?.provider || 'email',
          createdAt: initialSession.user.created_at,
          // Pass through raw Supabase fields so ProtectedRoute can read
          // `needs_password_setup` from user_metadata and detect Google
          // OAuth users via identities/app_metadata.
          user_metadata: initialSession.user.user_metadata || {},
          app_metadata: initialSession.user.app_metadata || {},
          identities: initialSession.user.identities || [],
        });
        // Sync profile to backend
        syncUserProfile(initialSession);
        // Track session start
        trackPublicEvent('session_start', initialSession.user.id, initialSession.user.email, {});
      } else {
        setSession(initialSession);
      }
      setLoading(false);
    });

    // Listen for auth state changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, currentSession) => {
        console.log('Auth state changed:', event);

        // Cluster A (Feb 2026): the invitation gate is an
        // *authentication-time* check, not a per-token-refresh
        // re-authorisation. Running it on TOKEN_REFRESHED was the
        // root cause of D5/D15 (transient denial → global signout).
        // Now: only SIGNED_IN runs the gate. Refresh failures route
        // through the central apiFetch banner instead.
        if (currentSession?.user && event === 'SIGNED_IN') {
          const ok = await checkAccess(currentSession);
          if (!ok) {
            // checkAccess already cleared user/session + opened modal.
            setLoading(false);
            return;
          }
        }

        // Magic-link landing fallback (May-19 2026):
        //   Supabase strips the path from `redirect_to` when the URL
        //   isn't in the project's Redirect URLs allowlist, dumping
        //   freshly invited users on `/#access_token=...` instead of
        //   `/setup-account#access_token=...`. We detect that case
        //   here — fresh SIGNED_IN, user_metadata.needs_password_setup
        //   still true, not yet on /setup-account — and forcibly route
        //   them to the setup page so the journey completes even with
        //   a misconfigured allowlist.
        //
        //   Google OAuth users are exempt (provider==='google'): their
        //   identity is already verified and they never need a password.
        if (event === 'SIGNED_IN' && currentSession?.user) {
          const meta = currentSession.user.user_metadata || {};
          const appMeta = currentSession.user.app_metadata || {};
          const idents = currentSession.user.identities || [];
          const isGoogle =
            appMeta.provider === 'google' ||
            idents.some((id) => id?.provider === 'google');
          const setupDone =
            typeof window !== 'undefined' &&
            localStorage.getItem('bayenah_password_setup_complete') === 'true';
          const needsSetup = !isGoogle && meta.needs_password_setup === true && !setupDone;
          if (needsSetup && typeof window !== 'undefined') {
            const onSetup = window.location.pathname === '/setup-account';
            if (!onSetup) {
              // Hard nav — clears the magic-link hash and lets
              // SetupAccountPage hydrate the session from storage.
              window.location.replace('/setup-account');
              return;
            }
          }

          // Access granted + no setup needed → close the modal and
          // route the user to /chat. handleLogin in AuthModal used to
          // do this itself with `window.location.href='/chat'`, but
          // that beat `checkAccess` to the history push and threw away
          // the gate's `authError` on denied sign-ins (May-21 2026 audit
          // Test 5). Owning routing here ensures the gate-error banner
          // is preserved when access is refused.
          if (typeof window !== 'undefined' && !needsSetup) {
            const path = window.location.pathname;
            // Cluster A (iter 2): the idle tab-lock sets a flag so a
            // freshly-locked tab on `/?idle=1` is NOT yanked back to
            // `/chat` by a still-valid session re-hydrating during
            // landing-page mount.
            //
            // Feb-07 2026 prod-bug fix: AuthModal.handleLogin /
            // handleSignInWithGoogle now CLEAR `bayenah_tab_locked`
            // before calling Supabase. So:
            //   • passive rehydration → flag still set → we bail (good)
            //   • explicit user sign-in → flag cleared → we redirect (good)
            // This unsticks the path the user reported as
            // "clicking Sign in does nothing".
            let tabLocked = false;
            try { tabLocked = sessionStorage.getItem('bayenah_tab_locked') === '1'; } catch (_e) { /* ignore */ }
            // Don't bounce setup-account (it's mid-flow), already-on-chat,
            // or a freshly-locked tab waiting for the user to Resume.
            if (path !== '/setup-account' && !path.startsWith('/chat') && !tabLocked) {
              setIsAuthModalOpen(false);
              // Cluster E.1.c (Feb 2026) deep-link preservation:
              // ProtectedRoute stamps `state.from = location` on its
              // <Navigate to="/">. React-router stores that under
              // `history.state.usr.from` (the `usr` key is RR v6's
              // internal namespace). If a preserved deep-link target
              // exists, replay its `${pathname}${search}` instead of
              // the string-literal '/chat'. Search is preserved per
              // the locked E.1.c plan; hash is NOT (Supabase OAuth
              // overloads `#access_token=…`).
              const from = window.history.state?.usr?.from;
              // Feb-07 2026: the idle-lock path also stashes a
              // `bayenah_tab_lock_from` key (see idle timer below).
              // That path uses `window.location.replace` (no react-
              // router history push), so `history.state.usr.from`
              // is empty — fall through to the stashed value before
              // defaulting to `/chat`.
              let stashedFrom = null;
              try { stashedFrom = sessionStorage.getItem('bayenah_tab_lock_from'); } catch (_e) { /* ignore */ }
              const replayPath = from?.pathname
                ? `${from.pathname}${from.search || ''}`
                : (stashedFrom || '/chat');
              // Clear the stash once consumed so a future idle-cycle
              // can't replay a stale path.
              try { sessionStorage.removeItem('bayenah_tab_lock_from'); } catch (_e) { /* ignore */ }
              // Use react-router-style hard nav — works whether or not
              // the page is wrapped in a Router (we're outside one when
              // the modal lives on the landing page).
              window.location.replace(replayPath);
              return;
            }
            // Already on /chat (or its subroutes) — just close the modal.
            if (isAuthModalOpen) setIsAuthModalOpen(false);
          }
        }

        setSession(currentSession);
        
        if (currentSession?.user) {
          const userData = {
            id: currentSession.user.id,
            email: currentSession.user.email,
            name: currentSession.user.user_metadata?.full_name || 
                  currentSession.user.user_metadata?.name ||
                  currentSession.user.email?.split('@')[0],
            avatar: currentSession.user.user_metadata?.avatar_url || 
                    currentSession.user.user_metadata?.picture,
            provider: currentSession.user.app_metadata?.provider || 'email',
            createdAt: currentSession.user.created_at,
            // See note above (init path) — kept in sync so reactive
            // consumers like ProtectedRoute see the same shape.
            user_metadata: currentSession.user.user_metadata || {},
            app_metadata: currentSession.user.app_metadata || {},
            identities: currentSession.user.identities || [],
          };
          setUser(userData);
          
          // On sign in, clear stale data first, then sync profile and migrate local chats
          if (event === 'SIGNED_IN') {
            // PRIVACY: Clear any leftover chat data from a different user
            const previousUserId = localStorage.getItem('bayenah-last-user-id');
            if (previousUserId && previousUserId !== currentSession.user.id) {
              console.log('[Bayenah] New user detected, clearing previous user data');
              localStorage.removeItem('legal-advisor-chats');
              localStorage.removeItem('legal-advisor-chat-history');
              localStorage.removeItem('legal-advisor-active-chat-id');
              localStorage.removeItem('legal-advisor-message-count');
              localStorage.removeItem('bayenah-message-feedback');
            }
            localStorage.setItem('bayenah-last-user-id', currentSession.user.id);
            
            await syncUserProfile(currentSession);
            await migrateLocalChats(currentSession);
            // Cluster A: cross-device active-chat restoration with a
            // 24h freshness window. Fire-and-forget — must NOT await
            // here. Awaiting blocks setLoading(false) if apiFetch ever
            // ends up queued (e.g. fresh-client token race) and the
            // banner listener happens to be unmounted. Restore is best-
            // effort and the page must mount even if the profile fetch
            // fails.
            (async () => {
              try {
                const { apiFetch } = await import('@/lib/apiFetch');
                const r = await apiFetch(`${BACKEND_URL}/api/auth/profile/me`, { method: 'GET' });
                if (r.ok) {
                  const profile = await r.json();
                  if (profile?.active_chat_id) {
                    const atMs = profile.active_chat_at
                      ? new Date(profile.active_chat_at).getTime()
                      : 0;
                    window.__BAYENAH_RESTORE_ACTIVE_CHAT = {
                      chatId: profile.active_chat_id,
                      atMs,
                    };
                  }
                  // Cluster B-3.d: cross-link the agent_session id so
                  // AgentPreviewPage can rehydrate the conversation
                  // envelope (not just the sidebar entry) on relogin.
                  if (profile?.active_session_id) {
                    const sAtMs = profile.active_session_at
                      ? new Date(profile.active_session_at).getTime()
                      : 0;
                    window.__BAYENAH_RESTORE_ACTIVE_SESSION = {
                      sessionId: profile.active_session_id,
                      atMs: sAtMs,
                    };
                  }
                }
              } catch (err) {
                console.warn('[active-chat-restore] profile fetch failed', err?.message || err);
              }
            })();
            // Track login event (use public endpoint - more reliable)
            trackPublicEvent('login', currentSession.user.id, currentSession.user.email, {});
          }
          
          // Track signup event (user just created)
          if (event === 'SIGNED_IN' && currentSession.user.created_at) {
            const createdAt = new Date(currentSession.user.created_at);
            const now = new Date();
            // If account was created within last 60 seconds, it's a signup
            if ((now - createdAt) < 60000) {
              trackPublicEvent('signup', currentSession.user.id, currentSession.user.email, { 
                signup_method: currentSession.user.app_metadata?.provider || 'email'
              });
            }
          }
        } else {
          setUser(null);
          // PRIVACY: On sign out, immediately clear any user-specific localStorage
          // This prevents the save effect in ChatContext from writing stale data back
          if (event === 'SIGNED_OUT') {
            localStorage.removeItem('legal-advisor-chats');
            localStorage.removeItem('legal-advisor-chat-history');
            localStorage.removeItem('legal-advisor-active-chat-id');
            localStorage.removeItem('legal-advisor-message-count');
            localStorage.removeItem('bayenah-message-feedback');
            localStorage.removeItem('bayenah-last-user-id');
            // Reset the setup-account flag so the NEXT user on this
            // browser still gets routed through /setup-account on their
            // first magic-link visit. Without this, a second user
            // sharing a device would skip straight to /chat without
            // ever setting their own password.
            localStorage.removeItem('bayenah_password_setup_complete');
          }
        }
        
        setLoading(false);
      }
    );

    return () => {
      subscription?.unsubscribe();
    };
  }, []);

  // Idle timeout — lock THIS tab after 30 minutes of inactivity.
  //
  // Cluster A (Feb 2026, iter 2): tab-local lock instead of
  // supabase.auth.signOut({scope:'local'}). The earlier scope:'local'
  // approach clears the project's localStorage key, which fires a
  // `storage` event that supabase-js v2's multi-tab listener uses to
  // emit SIGNED_OUT in every sibling tab on the same origin. That
  // defeats the "tab not device" intent of the policy — it actually
  // signed out the user's active tab too.
  //
  // New design: clear *only* this tab's in-memory auth state and
  // redirect to `/?idle=1`. Supabase auth storage is untouched, so
  //   - sibling tabs continue rendering normally (their in-memory
  //     state isn't affected because no SIGNED_OUT event fires);
  //   - other devices remain authenticated;
  //   - when the locked tab navigates back to /chat, AuthContext's
  //     initial `supabase.auth.getSession()` reads the still-valid
  //     token from storage and re-authenticates with zero password
  //     re-entry.
  //
  // Trade-off: the refresh token is no longer revoked on idle. If
  // your threat model requires server-side session expiry on idle,
  // use the explicit Logout button (which still calls
  // signOut({scope:'global'})).
  //
  // The 30-min window can be overridden in QA via
  // `window.__BAYENAH_QA_IDLE_TIMEOUT_MS` set by the QA harness.
  const DEFAULT_IDLE_TIMEOUT_MS = 30 * 60 * 1000;
  const IDLE_TIMEOUT_MS = (
    typeof window !== 'undefined' && Number(window.__BAYENAH_QA_IDLE_TIMEOUT_MS)
  ) || DEFAULT_IDLE_TIMEOUT_MS;
  const isAuthForIdle = !!user && !!session;
  useEffect(() => {
    if (!isAuthForIdle) return undefined;
    const KEY = 'bayenah_last_active';
    const bump = () => {
      try { sessionStorage.setItem(KEY, Date.now().toString()); } catch (_e) { /* ignore */ }
    };
    bump();
    const events = ['mousedown', 'keydown', 'scroll', 'touchstart', 'click'];
    events.forEach((e) => window.addEventListener(e, bump, { passive: true }));
    const tick = setInterval(() => {
      let last = 0;
      try { last = Number(sessionStorage.getItem(KEY) || 0); } catch (_e) { /* ignore */ }
      if (last && Date.now() - last > IDLE_TIMEOUT_MS) {
        try { sessionStorage.removeItem(KEY); } catch (_e) { /* ignore */ }
        // Cluster A (iter 2): tab-local lock.
        //  • Do NOT call supabase.auth.signOut (would broadcast SIGNED_OUT
        //    to sibling tabs via the storage event).
        //  • Do NOT remove the supabase localStorage key for the same
        //    reason.
        //  • Set a tab-local lock flag in sessionStorage; the SIGNED_IN
        //    auto-redirect (above) consults this flag and bails so the
        //    locked tab stays on `/?idle=1` instead of being yanked
        //    straight back to `/chat` by the still-valid session.
        //  • Clear React state in this tab; ProtectedRoute will then
        //    bounce to '/'.
        try { sessionStorage.setItem('bayenah_tab_locked', '1'); } catch (_e) { /* ignore */ }
        // Feb-07 2026 prod-bug fix: stash the page the user was on so
        // both the Resume CTA and the explicit Sign-in path can land
        // them back on `${pathname}${search}` instead of the bare
        // `/chat`. Matches the E.1.c path-preservation contract used
        // by ProtectedRoute's `state.from` capture.
        try {
          const here = window.location.pathname + (window.location.search || '');
          // Only stash if it's a real in-app path (avoid stashing the
          // landing itself or the idle URL — those would loop the
          // user back to the locked screen on Resume).
          // Note: post-Phase-1 the idle URL is /signin?idle=1 (the
          // static landing at / no longer carries the Resume banner).
          if (here && !here.startsWith('/signin?idle=') && !here.startsWith('/?idle=') && here !== '/' && here !== '/signin') {
            sessionStorage.setItem('bayenah_tab_lock_from', here);
          }
        } catch (_e) { /* ignore */ }
        setUser(null);
        setSession(null);
        if (typeof window !== 'undefined') {
          // Phase-1 retarget: the static landing at / does NOT render
          // the LandingPage React component, so the Resume banner is
          // unreachable there. Route to /signin?idle=1 — same Resume
          // banner UX, same code path; Supabase storage is untouched
          // so the user resumes with no password re-entry.
          window.location.replace('/signin?idle=1');
        }
      }
    }, Math.max(1000, Math.min(60_000, Math.floor(IDLE_TIMEOUT_MS / 30))));
    return () => {
      clearInterval(tick);
      events.forEach((e) => window.removeEventListener(e, bump));
    };
  }, [isAuthForIdle, IDLE_TIMEOUT_MS]);

  // Sync user profile to backend (de-duplicated per session id to avoid
  // double calls between getSession() and onAuthStateChange's SIGNED_IN event,
  // and to avoid React StrictMode duplicate dev-only invocations).
  const syncedSessionIdsRef = useRef(new Set());
  const syncUserProfile = async (currentSession) => {
    if (!currentSession?.access_token) return;

    // De-dupe: skip if we already synced this session in this tab
    const sessionKey = currentSession.user?.id + ':' + currentSession.access_token.slice(-12);
    if (syncedSessionIdsRef.current.has(sessionKey)) return;
    syncedSessionIdsRef.current.add(sessionKey);

    try {
      const response = await fetch(`${BACKEND_URL}/api/auth/profile/sync`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${currentSession.access_token}`,
        },
        body: JSON.stringify({
          name: currentSession.user.user_metadata?.full_name ||
                currentSession.user.user_metadata?.name,
          avatar_url: currentSession.user.user_metadata?.avatar_url ||
                      currentSession.user.user_metadata?.picture,
        }),
      });

      if (!response.ok) {
        if (response.status === 401) {
          console.warn('Profile sync unauthorized; skipping');
          return;
        }
        if (response.status === 429) {
          // Rate-limited — silent; will retry on next session refresh.
          // Avoid reading the body (some browsers/interceptors leave it drained).
          console.warn('Profile sync rate-limited; will retry on next session refresh');
          return;
        }
        // Defensive: clone before reading so any prior body consumption
        // (extensions, devtools, fetch interceptors) does not crash us.
        let errorText = '';
        try {
          errorText = await response.clone().text();
        } catch {
          // body was unreadable — fall back to statusText
          errorText = response.statusText || '';
        }
        console.error('Failed to sync profile:', errorText || response.statusText);
      }
    } catch (error) {
      console.error('Error syncing profile:', error);
    }
  };

  // Migrate local chats to cloud on first login
  // ONLY migrates guest chats that belong to NO user (anonymous browsing before signup)
  const migrateLocalChats = async (currentSession) => {
    if (!currentSession?.access_token) return;
    
    // PRIVACY: Do NOT migrate if a different user was previously logged in
    // (the localStorage data from the previous user should have been cleared on sign-in)
    const previousUserId = localStorage.getItem('bayenah-last-user-id');
    if (previousUserId && previousUserId !== currentSession.user.id) {
      // Stale data from another user — clear it, don't migrate
      localStorage.removeItem('legal-advisor-chats');
      return;
    }
    
    const localChats = localStorage.getItem('legal-advisor-chats');
    if (!localChats) return;
    
    try {
      const chats = JSON.parse(localChats);
      if (!chats || chats.length === 0) return;
      
      console.log(`Migrating ${chats.length} local chats to cloud...`);
      
      const response = await fetch(`${BACKEND_URL}/api/auth/chats/migrate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${currentSession.access_token}`,
        },
        body: JSON.stringify({ chats }),
      });
      
      if (response.ok) {
        // Clear local storage after successful migration
        localStorage.removeItem('legal-advisor-chats');
        console.log('Chat migration complete');
        // Trigger a page reload to fetch cloud chats
        window.location.reload();
      } else {
        console.error('Failed to migrate chats:', await response.text());
      }
    } catch (error) {
      console.error('Error migrating chats:', error);
    }
  };

  // Google Sign In (Primary)
  const signInWithGoogle = useCallback(async () => {
    if (!isSupabaseConfigured()) {
      throw new Error('Authentication not configured');
    }
    
    const { data, error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: `${window.location.origin}/chat`,
        queryParams: {
          access_type: 'offline',
          prompt: 'consent',
        },
      },
    });
    
    if (error) throw error;
    return data;
  }, []);

  // Email Sign Up
  const signUpWithEmail = useCallback(async (email, password, name) => {
    if (!isSupabaseConfigured()) {
      throw new Error('Authentication not configured');
    }
    
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: {
          full_name: name || email.split('@')[0],
        },
      },
    });
    
    if (error) throw error;
    
    // Only redirect if Supabase returned a session (email confirmation NOT required)
    // If data.user exists but data.session is null, email confirmation is pending
    if (data.session) {
      setIsAuthModalOpen(false);
      window.location.href = '/chat';
    }
    // If no session, the AuthModal will handle showing the confirmation screen
    
    return data;
  }, []);

  // Email Sign In
  const signInWithEmail = useCallback(async (email, password) => {
    if (!isSupabaseConfigured()) {
      throw new Error('Authentication not configured');
    }
    
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    
    if (error) throw error;
    
    // Close modal and redirect on success
    if (data.user) {
      setIsAuthModalOpen(false);
      // Redirect to chat after login
      window.location.href = '/chat';
    }
    
    return data;
  }, []);

  // Password Reset
  const resetPassword = useCallback(async (email) => {
    if (!isSupabaseConfigured()) {
      throw new Error('Authentication not configured');
    }
    
    const { data, error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/reset-password`,
    });
    
    if (error) throw error;
    return data;
  }, []);

  // Clear ALL user-specific localStorage data — PRIVACY CRITICAL
  const clearAllLocalStorage = useCallback(() => {
    localStorage.removeItem('legal-advisor-chats');
    localStorage.removeItem('legal-advisor-chat-history');
    localStorage.removeItem('legal-advisor-active-chat-id');
    localStorage.removeItem('legal-advisor-message-count');
    localStorage.removeItem('bayenah-message-feedback');
    localStorage.removeItem('bayenah-last-user-id');
  }, []);

  // Logout - clears auth state and redirects to landing
  const logout = useCallback(async () => {
    if (!isSupabaseConfigured()) {
      setUser(null);
      setSession(null);
      clearAllLocalStorage();
      return;
    }

    try {
      const { error } = await supabase.auth.signOut({ scope: 'global' });
      if (error) throw error;
    } finally {
      setUser(null);
      setSession(null);
      clearAllLocalStorage();

      // Redirect to landing page without showing auth modal
      // Use replace to prevent back button from going to protected route
      try {
        console.warn('[AUTH-REDIRECT] AuthContext.logout → window.location.href = "/"',
          { reason: 'logout completed, returning to landing' });
      } catch (_e) { /* noop */ }
      window.location.href = '/';
    }
  }, [clearAllLocalStorage]);

  // Open auth modal
  const openAuthModal = useCallback((mode = 'signup') => {
    setAuthModalMode(mode);
    setIsAuthModalOpen(true);
  }, []);

  // Close auth modal
  const closeAuthModal = useCallback(() => {
    setIsAuthModalOpen(false);
  }, []);

  // Get access token for API calls
  const getAccessToken = useCallback(async () => {
    if (!session?.access_token) return null;
    
    // Check if token needs refresh
    const { data, error } = await supabase.auth.getSession();
    if (error) {
      console.error('Error refreshing session:', error);
      return null;
    }
    
    return data.session?.access_token || null;
  }, [session]);

  // Track a question event
  const trackQuestion = useCallback(async (questionText, responseLength) => {
    if (!user?.id || !user?.email) return;
    
    // Use public endpoint for reliable tracking
    await trackPublicEvent('question', user.id, user.email, {
      question_text: questionText?.substring(0, 500), // Limit length
      response_length: responseLength,
    });
  }, [user]);

  const isAuthenticated = !!user && !!session;

  return (
    <AuthContext.Provider value={{
      user,
      session,
      loading,
      isAuthenticated,
      isAuthModalOpen,
      setIsAuthModalOpen,
      authModalMode,
      authError,
      setAuthError,
      signInWithGoogle,
      signUpWithEmail,
      signInWithEmail,
      resetPassword,
      logout,
      // Jul 2026 fix: TopBar and SettingsPage destructure `signOut` from
      // this context, but only `logout` was exported — their sign-out
      // buttons called `undefined` and silently did nothing, leaving the
      // session live (so "Sign in" dropped users straight back into the
      // account). Alias keeps every consumer working.
      signOut: logout,
      openAuthModal,
      closeAuthModal,
      setAuthModalMode,
      getAccessToken,
      trackQuestion,
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
